import { InfoImage } from '@infinex-private/connect-web-shared/components';
import { ArrowUpIcon, ArrowDownIcon } from '@infinex/ui-icons';
import { cn } from '@infinex/ui-styles';
import { Text } from '@infinex/ui/components';

import type { NormalizedAssetDiff as AssetDiffItemData } from './normalizeChainResponse';

type AssetDiffItemProps = AssetDiffItemData;

export function AssetDiffItem({
  asset,
  amount,
  usdValue,
  isReceiving,
  tokenId,
  isArbitraryToken,
}: AssetDiffItemProps) {
  const isNFT = asset.type === 'nft';

  // Use pre-formatted amounts from normalization
  const formattedAmount = amount;

  // Format USD value if not "0"
  const formattedUsd = usdValue === '0' ? '$0.00' : `$${usdValue}`;

  const icon = isReceiving ? (
    <ArrowDownIcon fill="positive" className="bg-fillPositive" />
  ) : (
    <ArrowUpIcon fill="critical" className="bg-fillCritical" />
  );

  return (
    <div className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
      <div className="flex flex-1 items-center gap-3">
        <InfoImage
          src={asset.logoUrl || ''}
          alt={asset.symbol || 'NFT'}
          className={isNFT ? 'rounded-lg' : 'rounded-full'} // Square for NFTs
          size={40}
          haloBg="#1C1E21"
          emblemPosition="top-left"
          badgeSize={16}
          haloSize={2}
          customEmblemIcon={icon}
        />

        {/* LEFT: stack "Sending/Receiving" and the token name */}
        <div className="flex flex-1 flex-col">
          <Text className="text-emphasis block text-xs font-medium">
            {isReceiving ? 'Receiving' : 'Sending'}
            {isNFT ? ' NFT' : ''}
          </Text>
          <Text className="text-default block text-base font-semibold">
            {asset.name || asset.symbol || 'Unknown NFT'}
          </Text>
          {isNFT && tokenId && (
            <Text className="text-secondary block text-xs">
              {isArbitraryToken
                ? 'Random token from collection'
                : `#${tokenId}`}
            </Text>
          )}
        </div>

        {/* RIGHT: stack token amount and USD value */}
        <div className="flex flex-col items-end text-right">
          <Text
            className={cn(
              'block text-base font-semibold tabular-nums',
              isReceiving ? 'text-positiveOnFill' : 'text-critical'
            )}
          >
            {isNFT
              ? // For NFTs, show quantity without +/- prefix
                amount !== '1'
                ? `${amount}x`
                : '1x'
              : // For fungible tokens, show with +/- prefix
                `${isReceiving ? '+' : '-'}${formattedAmount}`}
          </Text>
          {(!isNFT || (usdValue !== '0' && usdValue !== '0.00')) && (
            <Text className="text-secondary block text-xs tabular-nums">
              {formattedUsd}
            </Text>
          )}
        </div>
      </div>
    </div>
  );
}
